import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { LucideIcon } from "lucide-react";
import { Ban, ChevronRight, Eye, FileText, FlaskConical, Gauge, Gavel, Globe, Leaf, Mail, Pencil, Plus, Save, Share2, Trash2, Undo2, X, Zap } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { ComboContrato } from "../components/ComboContrato";
import { confettiBurst } from "../lib/confetti";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  actualizarEscenario,
  borrarEscenario,
  compartirEscenario,
  crearEscenario,
  descargarReporteEscenario,
  descompartirEscenario,
  getContratosEscenarios,
  getEscenarios,
  getUsuariosEscenarios,
  simularEscenario,
  type Escenario,
  type OperacionEscenario,
} from "@/lib/api";

const PRODUCTOS = ["Energía", "Potencia", "CEL", "i-REC"] as const;
// Mismos iconos/colores que el Reporte de Inventario.
const PRODUCTO_UI: Record<string, { icon: LucideIcon; activo: string }> = {
  Energía: { icon: Zap, activo: "bg-amber-100 text-amber-700 border-transparent" },
  Potencia: { icon: Gauge, activo: "bg-indigo-100 text-indigo-700 border-transparent" },
  CEL: { icon: Leaf, activo: "bg-emerald-100 text-emerald-700 border-transparent" },
  "i-REC": { icon: Globe, activo: "bg-sky-100 text-sky-700 border-transparent" },
};
// CEL e i-REC: el volumen capturado es POR MES (la simulación multiplica por
// los meses de vigencia).
const UNIDAD: Record<string, string> = {
  Energía: "MWh/h", Potencia: "MW-año", CEL: "CEL/mes", "i-REC": "i-REC/mes",
};
// Paleta para colorear contrapartes en el apilado (misma del Reporte de Inventario).
const PALETA = [
  "#4f46e5", "#0ea5e9", "#14b8a6", "#f59e0b", "#ef4444", "#8b5cf6",
  "#ec4899", "#22c55e", "#eab308", "#06b6d4", "#a855f7", "#f97316",
];
const AGRUPACIONES = [
  { key: "anio", label: "Año" },
  { key: "mes", label: "Mes" },
  { key: "dia", label: "Día" },
];
const HORIZONTES = [1, 3, 5, 10, 15, 25, 50];

const nf = new Intl.NumberFormat("es-MX", { maximumFractionDigits: 2 });
const money = (n: number, moneda = "USD") =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: moneda === "MXN" ? "MXN" : "USD",
  }).format(n);

const hoyIso = () => new Date().toISOString().slice(0, 10);
const finAnioIso = () => `${new Date().getFullYear()}-12-31`;

function opNueva(producto: OperacionEscenario["producto"]): OperacionEscenario {
  return {
    tipo: "compra", producto, contraparte: "", volumen: 0, precio: null,
    moneda: "USD", fecha_inicio: hoyIso(), fecha_fin: finAnioIso(), activa: true,
    estado: "activa", fecha_venta: null, fecha_cancelacion: null, aprobado: false,
    id_contrato: null, contrato_folio: null,
  };
}

const opActiva = (o: OperacionEscenario) => o.activa !== false;
const opEstado = (o: OperacionEscenario) => o.estado ?? "activa";
/** Requiere Business Case = dura más de 1 año (misma regla que el PDF). */
const requiereBC = (o: OperacionEscenario) => {
  if (!o.fecha_inicio || !o.fecha_fin) return false;
  const dias = (new Date(o.fecha_fin + "T00:00:00").getTime() -
    new Date(o.fecha_inicio + "T00:00:00").getTime()) / 86400000;
  return dias > 365;
};
// Vendidas/canceladas se guardan y se ven en la lista, pero salen de la simulación.
const opParticipa = (o: OperacionEscenario) => opActiva(o) && opEstado(o) === "activa";

const opValida = (o: OperacionEscenario) =>
  o.volumen > 0 && !!o.fecha_inicio && !!o.fecha_fin && o.fecha_fin >= o.fecha_inicio;

/** Meses calendario que toca la vigencia (jul–jul = 1, jul–dic = 6). */
function mesesVigencia(o: OperacionEscenario): number {
  const a = new Date(o.fecha_inicio + "T00:00:00");
  const b = new Date(o.fecha_fin + "T00:00:00");
  return (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth()) + 1;
}

/** Valor estimado: Energía = vol × horas de la vigencia × precio;
 *  CEL = vol MENSUAL × meses de la vigencia × precio; Potencia = vol × precio. */
function valorOp(o: OperacionEscenario): number | null {
  if (o.precio == null || !opValida(o)) return null;
  if (o.producto === "Energía") {
    const dias =
      (new Date(o.fecha_fin + "T00:00:00").getTime() -
        new Date(o.fecha_inicio + "T00:00:00").getTime()) /
        86400000 + 1;
    return o.volumen * dias * 24 * o.precio;
  }
  if (o.producto === "CEL" || o.producto === "i-REC") {
    return o.volumen * mesesVigencia(o) * o.precio;
  }
  return o.volumen * o.precio;
}

// Panel del dueño para compartir el escenario: agrega usuarios con permiso
// ver/editar, cambia permisos y revoca accesos.
function PanelCompartir({
  escenario,
  onCambio,
}: {
  escenario: Escenario;
  onCambio: () => void;
}) {
  const usuariosQ = useQuery({ queryKey: ["escenarios-usuarios"], queryFn: getUsuariosEscenarios });
  const [email, setEmail] = useState("");
  const [permisoNuevo, setPermisoNuevo] = useState<"ver" | "editar">("ver");
  const [error, setError] = useState<string | null>(null);

  const compartidos = escenario.compartidos ?? [];
  const disponibles = (usuariosQ.data ?? []).filter(
    (u) => !compartidos.some((c) => c.email === u.email.toLowerCase()),
  );

  const compartir = useMutation({
    mutationFn: (p: { email: string; permiso: "ver" | "editar" }) =>
      compartirEscenario(escenario.id, p.email, p.permiso),
    onSuccess: () => {
      setError(null);
      setEmail("");
      onCambio();
    },
    onError: (e) => setError(e instanceof Error ? e.message : "No se pudo compartir."),
  });
  const quitar = useMutation({
    mutationFn: (mail: string) => descompartirEscenario(escenario.id, mail),
    onSuccess: () => {
      setError(null);
      onCambio();
    },
    onError: (e) => setError(e instanceof Error ? e.message : "No se pudo quitar."),
  });

  return (
    <div className="bg-white border border-violet-200 rounded-lg p-4 space-y-3">
      <div className="text-sm font-medium text-slate-700 flex items-center gap-2">
        <Share2 className="w-4 h-4 text-violet-600" />
        Compartir "{escenario.nombre}"
      </div>

      {compartidos.length === 0 ? (
        <div className="text-sm text-slate-400">Aún no lo compartes con nadie.</div>
      ) : (
        <div className="divide-y border rounded">
          {compartidos.map((c) => (
            <div key={c.email} className="flex items-center gap-3 px-3 py-1.5 text-sm">
              <span className="flex-1">{c.email}</span>
              <select
                value={c.permiso}
                disabled={compartir.isPending}
                onChange={(e) =>
                  compartir.mutate({ email: c.email, permiso: e.target.value as "ver" | "editar" })
                }
                className="border rounded px-2 py-1 text-xs"
              >
                <option value="ver">Solo lectura</option>
                <option value="editar">Puede editar</option>
              </select>
              <button
                type="button"
                title="Dejar de compartir"
                disabled={quitar.isPending}
                onClick={() => quitar.mutate(c.email)}
                className="text-slate-400 hover:text-red-600 disabled:opacity-50"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <label className="text-sm">
          <span className="block text-xs text-slate-500 mb-1">Usuario</span>
          <select
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="border rounded px-2 py-1.5 text-sm min-w-64"
          >
            <option value="">— Elegir usuario —</option>
            {disponibles.map((u) => (
              <option key={u.email} value={u.email}>{u.email}</option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-xs text-slate-500 mb-1">Permiso</span>
          <select
            value={permisoNuevo}
            onChange={(e) => setPermisoNuevo(e.target.value as "ver" | "editar")}
            className="border rounded px-2 py-1.5 text-sm"
          >
            <option value="ver">Solo lectura</option>
            <option value="editar">Puede editar</option>
          </select>
        </label>
        <button
          type="button"
          disabled={!email || compartir.isPending}
          onClick={() => compartir.mutate({ email, permiso: permisoNuevo })}
          className="inline-flex items-center gap-2 bg-violet-600 text-white rounded px-3 py-2 text-sm hover:bg-violet-700 disabled:opacity-50"
        >
          <Plus className="w-4 h-4" /> {compartir.isPending ? "Compartiendo…" : "Compartir"}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-2.5 text-sm">{error}</div>
      )}
    </div>
  );
}

// Debounce simple para no simular en cada tecleo.
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function EscenariosPage() {
  const qc = useQueryClient();
  const listaQ = useQuery({ queryKey: ["escenarios"], queryFn: getEscenarios });
  const escenarios = listaQ.data?.items ?? [];
  // Catálogo de contratos reales para la columna "Contrato vinculado".
  const contratosQ = useQuery({
    queryKey: ["escenarios", "contratos"],
    queryFn: getContratosEscenarios,
    staleTime: 5 * 60 * 1000,
  });
  const contratos = contratosQ.data ?? [];
  // Solo se ofrecen los contratos del mismo producto y del mismo lado que la
  // operación: una venta de Potencia solo puede respaldarse con un contrato de
  // Potencia donde MPP es vendedor.
  // Y un mismo contrato respalda UNA sola operación por producto en el
  // escenario: los ya vinculados a otra fila del MISMO producto desaparecen del
  // combo. Un contrato híbrido (001NOR/MPP: Energía + CEL) sí puede respaldar
  // una fila de Energía y otra de CEL.
  const contratosPara = (o: OperacionEscenario, i: number) => {
    const usados = new Set(
      ops
        .filter((x, j) => j !== i && x.id_contrato != null && x.producto === o.producto)
        .map((x) => x.id_contrato as number),
    );
    return contratos.filter(
      (c) => c.lado === o.tipo && c.productos.includes(o.producto) && !usados.has(c.id_contrato),
    );
  };

  // --- Escenario en edición ---
  const [sel, setSel] = useState<number | "nuevo">("nuevo");
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [ops, setOps] = useState<OperacionEscenario[]>([opNueva("Energía")]);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [compartirAbierto, setCompartirAbierto] = useState(false);
  const [opsVisible, setOpsVisible] = useState(true);
  const [ocultarCerradas, setOcultarCerradas] = useState(true);
  // Filtro de la tabla por producto (null = todos); solo oculta filas, no afecta la simulación.
  const [filtroProducto, setFiltroProducto] = useState<string | null>(null);

  const cargar = (e: Escenario) => {
    setSel(e.id);
    setNombre(e.nombre);
    setDescripcion(e.descripcion ?? "");
    setOps(e.operaciones.length ? e.operaciones : [opNueva("Energía")]);
    setError(null);
    setAviso(null);
    setCompartirAbierto(false);
  };
  const nuevo = () => {
    setSel("nuevo");
    setNombre("");
    setDescripcion("");
    setOps([opNueva("Energía")]);
    setError(null);
    setAviso(null);
    setCompartirAbierto(false);
  };

  // Permiso del usuario sobre el escenario seleccionado.
  const escSel = sel === "nuevo" ? null : escenarios.find((x) => x.id === sel) ?? null;
  const permiso = escSel?.permiso ?? "dueño";
  const soloLectura = permiso === "ver";
  // En el escenario que sale en el correo diario (Configuración → Envío de
  // correo de escenarios) una operación (venta O compra, desde 1-oct-2026) solo
  // se marca vendida si ya tiene el contrato real que la respalda.
  const esCorreo = escSel?.es_correo === true;
  const ventaSinContrato = (o: OperacionEscenario) =>
    esCorreo && !o.id_contrato && !o.contrato_folio;

  const guardar = useMutation({
    mutationFn: () => {
      const payload = { nombre: nombre.trim(), descripcion: descripcion.trim() || null, operaciones: ops.filter(opValida) };
      // En modo solo-lectura, "Guardar copia" crea un escenario PROPIO nuevo.
      return sel === "nuevo" || soloLectura
        ? crearEscenario(payload)
        : actualizarEscenario(sel, payload);
    },
    onSuccess: (e) => {
      setSel(e.id);
      setError(null);
      setAviso(
        soloLectura
          ? `Copia guardada como escenario propio "${e.nombre}".`
          : `Escenario "${e.nombre}" guardado (${e.n_operaciones} operaciones).`,
      );
      qc.invalidateQueries({ queryKey: ["escenarios"] });
    },
    onError: (e) => {
      setAviso(null);
      setError(e instanceof Error ? e.message : "No se pudo guardar.");
    },
  });

  const borrar = useMutation({
    mutationFn: (id: number) => borrarEscenario(id),
    onSuccess: () => {
      nuevo();
      qc.invalidateQueries({ queryKey: ["escenarios"] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : "No se pudo borrar."),
  });

  const reporte = useMutation({
    mutationFn: (id: number) => descargarReporteEscenario(id),
    onError: (e) => setError(e instanceof Error ? e.message : "No se pudo generar el PDF."),
  });

  // --- Edición de operaciones ---
  const setOp = (i: number, patch: Partial<OperacionEscenario>) =>
    setOps((prev) => prev.map((o, j) => (j === i ? { ...o, ...patch } : o)));
  const quitarOp = (i: number) => setOps((prev) => prev.filter((_, j) => j !== i));

  // --- Simulación ---
  const [producto, setProducto] = useState<string>("Energía");
  const [agrupacion, setAgrupacion] = useState("mes");
  const [horizonte, setHorizonte] = useState(1);
  // Default ON (pedido 2026-09-03): sin el histórico, la cartera de meses ya
  // transcurridos (p. ej. CEL de abril) desaparece de la gráfica y confunde.
  const [incluirPasado, setIncluirPasado] = useState(true);
  const [energiaAgrupada, setEnergiaAgrupada] = useState(true);
  const [apilar, setApilar] = useState(false);

  const opsValidas = useMemo(() => ops.filter(opValida), [ops]);
  // Solo las ACTIVAS (ni vendidas ni canceladas) entran a la simulación.
  const opsSimulables = useMemo(() => opsValidas.filter(opParticipa), [opsValidas]);
  const opsJson = useDebounced(JSON.stringify(opsSimulables), 600);

  const simQ = useQuery({
    queryKey: ["escenario-sim", producto, agrupacion, horizonte, incluirPasado, energiaAgrupada, opsJson],
    queryFn: () =>
      simularEscenario({
        producto, agrupacion, horizonte,
        incluir_pasado: incluirPasado,
        energia_agrupada: producto === "Energía" ? energiaAgrupada : false,
        operaciones: JSON.parse(opsJson),
      }),
  });
  const sim = simQ.data;
  const puntos = sim?.puntos ?? [];
  const unidad = sim?.unidad ?? "";
  const series = sim?.series ?? [];
  const conPosicion = sim?.con_posicion ?? false; // Energía, Potencia y CEL
  // Color por contraparte (las series de escenario usan el color de su contraparte base).
  const cpsBase = Array.from(
    new Set(series.map((s) => s.contraparte.replace(/ · escenario$/, ""))),
  );
  const colorDe = (cp: string) =>
    PALETA[Math.max(0, cpsBase.indexOf(cp.replace(/ · escenario$/, ""))) % PALETA.length];

  // Totales estimados por moneda (compras restan, ventas suman).
  const valorPorMoneda = useMemo(() => {
    const acc: Record<string, number> = {};
    for (const o of opsSimulables) {
      const v = valorOp(o);
      if (v == null) continue;
      const m = o.moneda ?? "USD";
      acc[m] = (acc[m] ?? 0) + (o.tipo === "venta" ? v : -v);
    }
    return acc;
  }, [opsSimulables]);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Escenarios de compra/venta</h1>
        <p className="text-sm text-slate-500 mt-1">
          Simula operaciones hipotéticas de Energía, Potencia o CELs sobre la cartera real y
          guarda tus escenarios para consultarlos después. Solo tú ves tus escenarios.
        </p>
      </div>

      {/* ---- Selector / guardado ---- */}
      <div className="bg-white border rounded-lg p-4 flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="block text-xs text-slate-500 mb-1">Escenarios</span>
          <select
            value={sel === "nuevo" ? "nuevo" : String(sel)}
            onChange={(e) => {
              if (e.target.value === "nuevo") nuevo();
              else {
                const esc = escenarios.find((x) => x.id === Number(e.target.value));
                if (esc) cargar(esc);
              }
            }}
            className="border rounded px-2 py-1.5 text-sm min-w-56"
          >
            <option value="nuevo">— Nuevo escenario —</option>
            <optgroup label="Míos">
              {escenarios.filter((e) => e.es_mio).map((e) => (
                <option key={e.id} value={e.id}>
                  {e.es_correo ? "✉ " : ""}{e.nombre} ({e.n_operaciones} ops
                  {e.compartidos?.length ? ` · compartido con ${e.compartidos.length}` : ""}
                  {e.es_correo ? " · correo diario" : ""})
                </option>
              ))}
            </optgroup>
            {escenarios.some((e) => !e.es_mio) && (
              <optgroup label="Compartidos conmigo">
                {escenarios.filter((e) => !e.es_mio).map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.es_correo ? "✉ " : ""}{e.nombre} — de {e.creado_por.split("@")[0]} (
                    {e.permiso === "editar" ? "puede editar" : "solo lectura"}
                    {e.es_correo ? " · correo diario" : ""})
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </label>
        <label className="text-sm flex-1 min-w-56">
          <span className="block text-xs text-slate-500 mb-1">Nombre *</span>
          <input
            type="text"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="p. ej. Venta 5 MW a Luxem 2027"
            className="border rounded px-2 py-1.5 text-sm w-full"
          />
        </label>
        <label className="text-sm flex-1 min-w-56">
          <span className="block text-xs text-slate-500 mb-1">Descripción</span>
          <input
            type="text"
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            className="border rounded px-2 py-1.5 text-sm w-full"
          />
        </label>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={guardar.isPending || !nombre.trim()}
            onClick={() => guardar.mutate()}
            title={soloLectura ? "Este escenario es solo lectura: se guarda una copia tuya." : undefined}
            className="inline-flex items-center gap-2 bg-blue-600 text-white rounded px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />{" "}
            {guardar.isPending ? "Guardando…" : soloLectura ? "Guardar copia" : "Guardar"}
          </button>
          <button
            type="button"
            onClick={nuevo}
            className="inline-flex items-center gap-2 border rounded px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"
          >
            <Plus className="w-4 h-4" /> Nuevo
          </button>
          {sel !== "nuevo" && (
            <button
              type="button"
              disabled={reporte.isPending}
              onClick={() => reporte.mutate(sel)}
              title="Descargar el Reporte Comercial (PDF: Pipeline de Compras y Ventas)"
              className="inline-flex items-center gap-2 border border-slate-300 rounded px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <FileText className="w-4 h-4" /> {reporte.isPending ? "Generando…" : "Reporte PDF"}
            </button>
          )}
          {escSel?.es_mio && (
            <button
              type="button"
              onClick={() => setCompartirAbierto((v) => !v)}
              aria-pressed={compartirAbierto}
              className={`inline-flex items-center gap-2 border rounded px-3 py-2 text-sm ${
                compartirAbierto
                  ? "border-violet-400 bg-violet-50 text-violet-700"
                  : "border-violet-300 text-violet-700 hover:bg-violet-50"
              }`}
            >
              <Share2 className="w-4 h-4" /> Compartir
              {escSel.compartidos?.length ? ` (${escSel.compartidos.length})` : ""}
            </button>
          )}
          {sel !== "nuevo" && permiso === "dueño" && (
            <button
              type="button"
              disabled={borrar.isPending}
              onClick={() => {
                if (confirm(`¿Borrar el escenario "${nombre}"?\n\nEsta acción no se puede deshacer.`)) {
                  borrar.mutate(sel);
                }
              }}
              className="inline-flex items-center gap-2 border border-red-300 text-red-700 rounded px-3 py-2 text-sm hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" /> Borrar
            </button>
          )}
        </div>
      </div>

      {escSel && !escSel.es_mio && (
        <div className={`rounded border p-3 text-sm flex items-center gap-2 ${
          soloLectura
            ? "bg-slate-50 border-slate-200 text-slate-600"
            : "bg-violet-50 border-violet-200 text-violet-800"
        }`}>
          {soloLectura ? <Eye className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
          Compartido por <b>{escSel.creado_por}</b> —{" "}
          {soloLectura
            ? "solo lectura: puedes simular y guardar una copia tuya, pero no modificar el original."
            : "puedes editar y guardar cambios (borrar y compartir siguen siendo del dueño)."}
        </div>
      )}

      {compartirAbierto && escSel?.es_mio && (
        <PanelCompartir
          escenario={escSel}
          onCambio={() => qc.invalidateQueries({ queryKey: ["escenarios"] })}
        />
      )}

      {aviso && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded p-3 text-sm">{aviso}</div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">{error}</div>
      )}

      {/* ---- Operaciones ---- */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <div
          className="px-4 py-2.5 bg-slate-50 flex items-center gap-2 cursor-pointer select-none hover:bg-slate-100/70"
          onClick={() => setOpsVisible((v) => !v)}
          title={opsVisible ? "Colapsar" : "Expandir"}
        >
          <ChevronRight
            className={`w-4 h-4 text-slate-400 transition-transform ${opsVisible ? "rotate-90" : ""}`}
          />
          <FlaskConical className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-medium text-slate-700">Operaciones hipotéticas</span>
          {esCorreo && (
            <span
              title="Este escenario se envía cada día como Reporte Comercial · Pipeline y Portafolio. Sus compras y ventas solo se pueden marcar como vendidas con un contrato vinculado."
              className="inline-flex items-center gap-1 text-[11px] font-medium text-sky-700 bg-sky-100 rounded-full px-2 py-0.5"
            >
              <Mail className="w-3 h-3" /> Correo diario · solo con contrato
            </span>
          )}
          {!opsVisible && (
            <span className="text-xs text-slate-400">
              — {ops.length} operación{ops.length === 1 ? "" : "es"} ({opsSimulables.length} en simulación)
            </span>
          )}
          {opsVisible && (
            <>
              <div
                onClick={(e) => e.stopPropagation()}
                title="Filtra la tabla por producto (la simulación no cambia)"
                className="ml-auto flex gap-1.5"
              >
                <button
                  type="button"
                  onClick={() => setFiltroProducto(null)}
                  aria-pressed={filtroProducto === null}
                  className={`inline-flex items-center rounded px-2.5 py-1 text-xs font-semibold border transition ${
                    filtroProducto === null
                      ? "bg-slate-200 text-slate-700 border-transparent"
                      : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  Todos
                </button>
                {PRODUCTOS.map((p) => {
                  const Icon = PRODUCTO_UI[p].icon;
                  const active = filtroProducto === p;
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setFiltroProducto(active ? null : p)}
                      aria-pressed={active}
                      className={`inline-flex items-center gap-1 rounded px-2.5 py-1 text-xs font-semibold border transition ${
                        active
                          ? PRODUCTO_UI[p].activo
                          : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" /> {p}
                    </button>
                  );
                })}
              </div>
              <label
                onClick={(e) => e.stopPropagation()}
                title="Oculta de la tabla las operaciones marcadas como vendidas o canceladas (se conservan en el escenario)"
                className="inline-flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer select-none"
              >
                <input
                  type="checkbox"
                  checked={ocultarCerradas}
                  onChange={(e) => setOcultarCerradas(e.target.checked)}
                  className="w-3.5 h-3.5 accent-slate-600 cursor-pointer"
                />
                Ocultar vendidas/canceladas
              </label>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOps((prev) => [...prev, opNueva((filtroProducto ?? producto) as OperacionEscenario["producto"])]);
                }}
                className="inline-flex items-center gap-1 text-sm text-blue-700 hover:underline"
              >
                <Plus className="w-4 h-4" /> Agregar operación
              </button>
            </>
          )}
        </div>
        {opsVisible && (
        <div className="overflow-x-auto border-t">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b">
                <th className="px-3 py-2">Activa</th>
                <th className="px-3 py-2">Tipo</th>
                <th className="px-3 py-2">Producto</th>
                <th className="px-3 py-2">Contraparte</th>
                <th className="px-3 py-2" title="Contrato real (Administrar Contratos) que respalda la operación. Se listan solo los del mismo producto y tipo (compra/venta según el rol de MPP).">
                  Contrato vinculado
                </th>
                <th className="px-3 py-2 text-right">Volumen</th>
                <th className="px-3 py-2 text-right">Precio (opcional)</th>
                <th className="px-3 py-2">Moneda</th>
                <th className="px-3 py-2">Desde</th>
                <th className="px-3 py-2">Hasta</th>
                <th className="px-3 py-2 text-right">Valor estimado</th>
                <th className="px-3 py-2 text-center" title="Business Case aprobado por Comité — solo operaciones de más de 1 año">BC aprobado</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {ops.map((o, i) => {
                const valor = valorOp(o);
                const inactiva = !opActiva(o);
                const estado = opEstado(o);
                if (ocultarCerradas && (estado === "vendida" || estado === "cancelada")) return null;
                if (filtroProducto && o.producto !== filtroProducto) return null;
                return (
                  <tr
                    key={i}
                    className={`border-b last:border-0 ${
                      estado === "vendida"
                        ? (o.tipo === "compra" ? "bg-sky-50/70 opacity-80" : "bg-emerald-50/70 opacity-80")
                        : estado === "cancelada"
                          ? "bg-red-50/60 opacity-60"
                          : inactiva ? "opacity-50 bg-slate-50" : opValida(o) ? "" : "bg-amber-50/40"
                    }`}
                  >
                    <td className="px-3 py-1.5 text-center">
                      <input
                        type="checkbox"
                        checked={!inactiva}
                        onChange={(e) => setOp(i, { activa: e.target.checked })}
                        title="Las operaciones desactivadas se guardan con el escenario pero no entran a la simulación"
                        className="w-4 h-4 accent-emerald-600 cursor-pointer"
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      <select
                        value={o.tipo}
                        onChange={(e) => setOp(i, { tipo: e.target.value as OperacionEscenario["tipo"] })}
                        className={`border rounded px-2 py-1 text-sm font-medium ${
                          o.tipo === "compra" ? "text-indigo-700" : "text-amber-700"
                        }`}
                      >
                        <option value="compra">Compra</option>
                        <option value="venta">Venta</option>
                      </select>
                    </td>
                    <td className="px-3 py-1.5">
                      <select
                        value={o.producto}
                        onChange={(e) => setOp(i, { producto: e.target.value as OperacionEscenario["producto"] })}
                        className="border rounded px-2 py-1 text-sm"
                      >
                        {PRODUCTOS.map((p) => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-1.5">
                      <input
                        type="text"
                        value={o.contraparte ?? ""}
                        onChange={(e) => setOp(i, { contraparte: e.target.value })}
                        placeholder="(hipotética)"
                        className="border rounded px-2 py-1 text-sm w-36"
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      <ComboContrato
                        contratos={contratosPara(o, i)}
                        value={o.id_contrato ?? null}
                        folio={o.contrato_folio ?? null}
                        placeholder={ventaSinContrato(o) ? "Requerido para vender" : "Buscar contrato…"}
                        onChange={(c) =>
                          setOp(i, {
                            id_contrato: c?.id_contrato ?? null,
                            contrato_folio: c?.folio ?? null,
                            // Si la operación no tenía contraparte, tomar la del contrato.
                            ...(c && !(o.contraparte ?? "").trim() ? { contraparte: c.contraparte } : {}),
                          })
                        }
                      />
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <input
                        type="number"
                        min="0"
                        step="any"
                        value={o.volumen || ""}
                        onChange={(e) => setOp(i, { volumen: Number(e.target.value) })}
                        className="border rounded px-2 py-1 text-sm w-24 text-right"
                      />
                      <span className="text-xs text-slate-400 ml-1">{UNIDAD[o.producto]}</span>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <input
                        type="number"
                        min="0"
                        step="any"
                        value={o.precio ?? ""}
                        onChange={(e) =>
                          setOp(i, { precio: e.target.value === "" ? null : Number(e.target.value) })
                        }
                        className="border rounded px-2 py-1 text-sm w-28 text-right"
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      <select
                        value={o.moneda ?? "USD"}
                        onChange={(e) => setOp(i, { moneda: e.target.value })}
                        className="border rounded px-2 py-1 text-sm"
                      >
                        <option value="USD">USD</option>
                        <option value="MXN">MXN</option>
                      </select>
                    </td>
                    <td className="px-3 py-1.5">
                      <input
                        type="date"
                        value={o.fecha_inicio}
                        onChange={(e) => setOp(i, { fecha_inicio: e.target.value })}
                        className="border rounded px-2 py-1 text-sm"
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      <input
                        type="date"
                        value={o.fecha_fin}
                        onChange={(e) => setOp(i, { fecha_fin: e.target.value })}
                        className="border rounded px-2 py-1 text-sm"
                      />
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">
                      {valor == null ? "—" : money(valor, o.moneda)}
                    </td>
                    <td className="px-3 py-1.5 text-center">
                      {requiereBC(o) ? (
                        <input
                          type="checkbox"
                          checked={o.aprobado === true}
                          onChange={(e) => setOp(i, { aprobado: e.target.checked })}
                          title={o.aprobado
                            ? "Business Case aprobado — en el PDF sale 'Aprobado'"
                            : "Business Case por presentar — palomea cuando el Comité lo apruebe"}
                          className="w-4 h-4 accent-violet-600 cursor-pointer"
                        />
                      ) : (
                        <span className="text-slate-300" title="No requiere Business Case (≤ 1 año)">—</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="flex items-center gap-1.5 justify-end whitespace-nowrap">
                        {estado === "activa" && (
                          <>
                            <button
                              type="button"
                              disabled={ventaSinContrato(o)}
                              title={
                                ventaSinContrato(o)
                                  ? `Este escenario se envía por correo: elige antes el contrato que respalda la ${o.tipo === "compra" ? "compra" : "venta"}`
                                  : o.tipo === "compra"
                                    ? "Marcar como comprada (sale de la gráfica)"
                                    : "Marcar como vendida (sale de la gráfica)"
                              }
                              onClick={(e) => {
                                if (ventaSinContrato(o)) return;
                                confettiBurst(e.clientX, e.clientY);
                                setOp(i, { estado: "vendida", fecha_venta: hoyIso() });
                              }}
                              className={`hammer-btn text-slate-400 hover:text-emerald-700 ${
                                ventaSinContrato(o) ? "opacity-30 cursor-not-allowed hover:text-slate-400" : ""
                              }`}
                            >
                              <Gavel className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              title="Marcar como cancelada (sale de la gráfica)"
                              onClick={() => setOp(i, { estado: "cancelada", fecha_cancelacion: hoyIso() })}
                              className="text-slate-400 hover:text-red-600"
                            >
                              <Ban className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {estado === "vendida" && (
                          <>
                            {/* Una compra cerrada se etiqueta "Comprada" (azul); una venta, "Vendida" (verde). */}
                            <span
                              className={`inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-2 py-0.5 ${
                                o.tipo === "compra" ? "text-sky-700 bg-sky-100" : "text-emerald-700 bg-emerald-100"
                              }`}
                            >
                              <Gavel className="w-3 h-3" /> {o.tipo === "compra" ? "Comprada" : "Vendida"}
                            </span>
                            <input
                              type="date"
                              value={o.fecha_venta ?? ""}
                              onChange={(e) => setOp(i, { fecha_venta: e.target.value || null })}
                              title={o.tipo === "compra" ? "Fecha de la compra" : "Fecha de la venta"}
                              className="border rounded px-1.5 py-0.5 text-xs"
                            />
                            <button
                              type="button"
                              title="Deshacer venta (vuelve a la simulación)"
                              onClick={() => setOp(i, { estado: "activa", fecha_venta: null })}
                              className="text-slate-400 hover:text-slate-700"
                            >
                              <Undo2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {estado === "cancelada" && (
                          <>
                            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-red-700 bg-red-100 rounded-full px-2 py-0.5">
                              <Ban className="w-3 h-3" /> Cancelada
                            </span>
                            <input
                              type="date"
                              value={o.fecha_cancelacion ?? ""}
                              onChange={(e) => setOp(i, { fecha_cancelacion: e.target.value || null })}
                              title="Fecha de la cancelación"
                              className="border rounded px-1.5 py-0.5 text-xs"
                            />
                            <button
                              type="button"
                              title="Deshacer cancelación (vuelve a la simulación)"
                              onClick={() => setOp(i, { estado: "activa", fecha_cancelacion: null })}
                              className="text-slate-400 hover:text-slate-700"
                            >
                              <Undo2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        <button
                          type="button"
                          title="Quitar operación"
                          onClick={() => quitarOp(i)}
                          className="text-slate-400 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
        {opsVisible && (
        <div className="px-4 py-2 text-xs text-slate-500 border-t flex flex-wrap gap-x-6 gap-y-1">
          <span>
            {opsSimulables.length} de {ops.length} operaciones en la simulación (las inactivas,
            vendidas, canceladas e incompletas no se simulan, pero sí se guardan).
          </span>
          {ocultarCerradas && (() => {
            const n = ops.filter((o) => {
              const e = opEstado(o);
              return e === "vendida" || e === "cancelada";
            }).length;
            return n > 0 ? (
              <span className="text-slate-400">
                {n} operación{n === 1 ? "" : "es"} vendida{n === 1 ? "" : "s"}/cancelada{n === 1 ? "" : "s"} oculta{n === 1 ? "" : "s"} de la tabla.
              </span>
            ) : null;
          })()}
          {filtroProducto && (() => {
            const n = ops.filter((o) => o.producto !== filtroProducto).length;
            return n > 0 ? (
              <span className="text-slate-400">
                Mostrando solo {filtroProducto}: {n} operación{n === 1 ? "" : "es"} de otros productos oculta{n === 1 ? "" : "s"}.
              </span>
            ) : null;
          })()}
          {Object.entries(valorPorMoneda).map(([m, v]) => (
            <span key={m}>
              Flujo neto estimado: <b className={v >= 0 ? "text-emerald-700" : "text-red-700"}>{money(v, m)}</b>
              <span className="text-slate-400"> (ventas − compras, {m})</span>
            </span>
          ))}
        </div>
        )}
      </div>

      {/* ---- Controles de simulación ---- */}
      <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
        <div>
          <div className="text-[11px] font-medium text-slate-500 mb-1">Producto a graficar</div>
          <div className="flex gap-2">
            {PRODUCTOS.map((p) => {
              const Icon = PRODUCTO_UI[p].icon;
              const active = producto === p;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => setProducto(p)}
                  aria-pressed={active}
                  className={`inline-flex items-center gap-1 rounded px-3 py-1.5 text-sm font-semibold border transition ${
                    active
                      ? PRODUCTO_UI[p].activo
                      : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <Icon className="w-4 h-4" /> {p}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <div className="text-[11px] font-medium text-slate-500 mb-1">Agrupar por</div>
          <select
            value={agrupacion}
            onChange={(e) => setAgrupacion(e.target.value)}
            className="border border-slate-200 rounded px-3 py-1.5 text-sm bg-white"
          >
            {AGRUPACIONES.map((a) => (
              <option key={a.key} value={a.key}>{a.label}</option>
            ))}
          </select>
        </div>
        <div>
          <div className="text-[11px] font-medium text-slate-500 mb-1">Horizonte</div>
          <select
            value={horizonte}
            onChange={(e) => setHorizonte(Number(e.target.value))}
            className="border border-slate-200 rounded px-3 py-1.5 text-sm bg-white"
          >
            {HORIZONTES.map((h) => (
              <option key={h} value={h}>{h} {h === 1 ? "año" : "años"}</option>
            ))}
          </select>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-slate-600 select-none pb-1.5">
          <input type="checkbox" checked={incluirPasado} onChange={(e) => setIncluirPasado(e.target.checked)} className="w-4 h-4" />
          Incluir histórico
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-slate-600 select-none pb-1.5">
          <input type="checkbox" checked={apilar} onChange={(e) => setApilar(e.target.checked)} className="w-4 h-4" />
          Apilar por contraparte
        </label>
        {producto === "Energía" && (
          <label className="inline-flex items-center gap-2 text-sm text-slate-600 select-none pb-1.5">
            <input type="checkbox" checked={energiaAgrupada} onChange={(e) => setEnergiaAgrupada(e.target.checked)} className="w-4 h-4" />
            Agrupar la energía (MWh/h, no acumulada)
          </label>
        )}
      </div>

      {sim && sim.n_ops_otros_productos > 0 && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          {sim.n_ops_otros_productos} operación(es) de otros productos no se grafican aquí — cambia el
          producto a graficar para verlas.
        </div>
      )}

      {/* ---- Gráfica ---- */}
      <div className="bg-white border rounded-lg p-4">
        {simQ.isLoading && <div className="p-8 text-center text-sm text-slate-500">Simulando…</div>}
        {simQ.isError && (
          <div className="p-8 text-center text-sm text-red-600">{(simQ.error as Error).message}</div>
        )}
        {!simQ.isLoading && !simQ.isError && puntos.length === 0 && (
          <div className="p-8 text-center text-sm text-slate-500">
            No hay contratos reales ni operaciones de {producto} para graficar.
          </div>
        )}
        {puntos.length > 0 && (
          <>
            <div className="text-xs text-slate-500 mb-2">
              Unidad: {unidad} · barras claras = escenario · línea verde = inventario con escenario
            </div>
            <div style={{ width: "100%", height: 380 }}>
              <ResponsiveContainer>
                {/* barSize fijo con muchos puntos: con >150 categorías el ancho auto
                    de recharts cae a 0px y las barras desaparecen (25 años × mes) */}
                <ComposedChart
                  data={puntos}
                  barGap={0}
                  barSize={puntos.length > 150 ? Math.max(1, Math.floor(1500 / puntos.length / 2)) : undefined}
                  margin={{ top: 8, right: 16, left: 0, bottom: 4 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="etiqueta" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={64} minTickGap={8} />
                  <YAxis tick={{ fontSize: 12 }} width={64} tickFormatter={(v) => nf.format(v as number)} />
                  <Tooltip formatter={(v: number, name: string) => [`${nf.format(v)} ${unidad}`, name]} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {apilar ? (
                    series.map((s) => (
                      <Bar
                        key={s.key}
                        dataKey={s.key}
                        stackId={s.tipo}
                        fill={colorDe(s.contraparte)}
                        fillOpacity={s.esc ? 0.55 : 1}
                        name={`${s.tipo} · ${s.contraparte}`}
                      />
                    ))
                  ) : (
                    <>
                      <Bar dataKey="compras" name="Compras (real)" stackId="C" fill="#4f46e5" />
                      <Bar dataKey="compras_esc" name="Compras (escenario)" stackId="C" fill="#a5b4fc" />
                      <Bar dataKey="ventas" name="Ventas (real)" stackId="V" fill="#f59e0b" />
                      <Bar dataKey="ventas_esc" name="Ventas (escenario)" stackId="V" fill="#fcd34d" />
                    </>
                  )}
                  <Line
                    type="monotone" dataKey="inventario" name="Inventario real"
                    stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 4"
                    dot={false}
                  />
                  <Line
                    type="monotone" dataKey="inventario_esc" name="Inventario con escenario"
                    stroke="#059669" strokeWidth={2.5}
                    dot={puntos.length <= 60 ? { r: 3 } : false}
                  />
                  {conPosicion && (
                    <Line
                      type="monotone" dataKey="posicion_larga" name="Límite Posición larga"
                      stroke="#0284c7" strokeWidth={1.5} strokeDasharray="4 3" dot={false}
                    />
                  )}
                  {conPosicion && (
                    <Line
                      type="monotone" dataKey="posicion_corta" name="Límite Posición corta"
                      stroke="#dc2626" strokeWidth={1.5} strokeDasharray="4 3" dot={false}
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[11px] text-slate-400 mt-2">
              Las barras y la línea punteada muestran la cartera real; las barras claras y la línea
              verde agregan tus operaciones hipotéticas. Inventario positivo = MPP largo; negativo = corto.
              {conPosicion && (
                <>
                  {" "}Posición larga/corta = bandas sobre las compras (larga +15% &lt;1año / +20% ≥1año;
                  corta −5% &lt;1año / −10% ≥1año, según la duración de cada transacción).
                </>
              )}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
